import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    SlidersHorizontal,
    ShieldCheck,
    Activity,
    HeartPulse,
    MapPin,
    Star,
    ArrowRight,
    Stethoscope,
    Building2,
    Home as HomeIcon,
    Globe2,
    Sparkles,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import { fetchCenters, CHILE_REGIONS, MODALITIES } from "../lib/api";

const faqs = [
    {
        q: "¿Qué es un centro de rehabilitación de drogas?",
        a: "Es una institución con equipo clínico multidisciplinario que entrega tratamiento estructurado para personas con consumo problemático de alcohol u otras drogas. Puede funcionar en modalidad residencial, ambulatoria u online.",
    },
    {
        q: "¿Cuánto dura la rehabilitación de drogas en Chile?",
        a: "En formato residencial la duración habitual es de 3 a 9 meses. En ambulatorio se extiende entre 6 y 18 meses con seguimiento posterior. La duración exacta depende del tipo de sustancia y la severidad del consumo.",
    },
    {
        q: "¿Qué diferencia un centro verificado de uno no verificado?",
        a: "Los centros verificados han acreditado su infraestructura, equipo profesional titulado y protocolos clínicos. Cuentan con acreditación de la SEREMI de Salud y, en muchos casos, convenios con SENDA.",
    },
    {
        q: "¿Puedo encontrar centros cerca de mi ciudad?",
        a: "Sí. Nuestro directorio incluye más de 88 centros distribuidos en las 16 regiones de Chile, desde Arica hasta Magallanes. Puedes filtrar por región, ciudad y modalidad.",
    },
    {
        q: "¿Los centros atienden a mujeres y hombres?",
        a: "La mayoría son mixtos, pero también hay centros exclusivos para hombres o mujeres, así como programas específicos para adolescentes y adultos mayores.",
    },
];

const benefits = [
    {
        icon: ShieldCheck,
        title: "Centros verificados",
        text: "Cada ficha incluye acreditación clínica, equipo profesional y protocolos verificables.",
    },
    {
        icon: HeartPulse,
        title: "Atención integral",
        text: "Incluye tratamiento médico, psicológico, familiar y reinserción social progresiva.",
    },
    {
        icon: Activity,
        title: "Modalidades flexibles",
        text: "Residencial, ambulatorio intensivo y online: eliges según tu realidad clínica y laboral.",
    },
    {
        icon: Stethoscope,
        title: "Equipo multidisciplinario",
        text: "Psiquiatras, psicólogos y terapeutas especializados en adicciones y trastornos duales.",
    },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);

    // search form state
    const [searchRegion, setSearchRegion] = useState("");
    const [searchModality, setSearchModality] = useState("");

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetchCenters({ page: 1, limit: 6, is_verified: true })
            .then((data) => {
                if (mounted) setFeatured(data.centers || []);
            })
            .catch(() => {
                if (mounted) setFeatured([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    function onSubmit(e) {
        e.preventDefault();
        const params = new URLSearchParams();
        if (searchRegion && searchRegion !== "all") params.set("region", searchRegion);
        if (searchModality && searchModality !== "all") params.set("modality", searchModality);
        window.location.href = `/centros${params.toString() ? "?" + params.toString() : ""}`;
    }

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <>
            <Seo
                title="Encuentra el mejor centro de rehabilitación de drogas en Chile"
                description="Directorio verificado con más de 88 centros de rehabilitación de drogas en Chile. Filtra por región, modalidad y precio. Compara opciones residenciales, ambulatorias y online."
                path="/"
                keywords="centro de rehabilitación de drogas, centros de rehabilitación Chile, tratamiento de drogas, rehabilitación adicciones, clínica de desintoxicación"
                jsonLd={faqJsonLd}
            />

            {/* Hero */}
            <section
                data-testid="home-hero"
                className="noise bg-grain relative overflow-hidden border-b border-slate-800"
            >
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.12),transparent_55%)]" />
                <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-5 lg:px-8">
                    <div className="lg:col-span-3">
                        <div className="fade-up inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-300">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Directorio verificado 2026
                        </div>
                        <h1
                            data-testid="home-h1"
                            className="fade-up-delay-1 mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
                        >
                            Encuentra el mejor{" "}
                            <span className="text-cyan-400">
                                centro de rehabilitación de drogas
                            </span>{" "}
                            en Chile
                        </h1>
                        <p className="fade-up-delay-2 mt-5 max-w-2xl text-base text-slate-400 sm:text-lg">
                            Compara 88+ centros verificados por región, modalidad y
                            presupuesto. Información clínica, precios transparentes y
                            acceso directo a equipos profesionales para iniciar tu
                            tratamiento hoy.
                        </p>

                        <form
                            onSubmit={onSubmit}
                            data-testid="home-search-form"
                            className="fade-up-delay-3 relative mt-8 overflow-hidden rounded-2xl p-[1.5px]"
                        >
                            {/* Animated gradient border */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 -z-10 bg-[conic-gradient(from_90deg_at_50%_50%,rgba(6,182,212,0.55)_0%,rgba(56,189,248,0.35)_25%,rgba(6,182,212,0.15)_55%,rgba(6,182,212,0.55)_100%)]"
                            />
                            {/* Inner panel with glass effect */}
                            <div className="relative rounded-2xl bg-slate-950/80 p-5 backdrop-blur-xl">
                                {/* Cyan glow behind the form */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -left-8 -top-10 h-32 w-48 rounded-full bg-cyan-500/20 blur-3xl"
                                />
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -bottom-10 -right-4 h-28 w-40 rounded-full bg-sky-500/15 blur-3xl"
                                />

                                {/* Label header */}
                                <div className="relative mb-4 flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                        <SlidersHorizontal className="h-3.5 w-3.5" />
                                        Busca tu centro ideal
                                    </div>
                                    <span className="hidden rounded-full bg-cyan-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-300 sm:inline-block">
                                        Gratis
                                    </span>
                                </div>

                                <div className="relative grid gap-3 md:grid-cols-[1.2fr_1.2fr_auto]">
                                    <div className="flex flex-col gap-1.5">
                                        <label
                                            htmlFor="home-search-region"
                                            className="text-[11px] font-medium uppercase tracking-wider text-slate-400"
                                        >
                                            Región
                                        </label>
                                        <Select value={searchRegion} onValueChange={setSearchRegion}>
                                            <SelectTrigger
                                                id="home-search-region"
                                                data-testid="home-search-region"
                                                className="h-11 border-slate-800 bg-slate-900 text-slate-100 transition-colors focus:ring-cyan-500/50 data-[state=open]:border-cyan-500/40"
                                            >
                                                <SelectValue placeholder="Todas las regiones" />
                                            </SelectTrigger>
                                            <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                                                <SelectItem value="all">Todas las regiones</SelectItem>
                                                {CHILE_REGIONS.map((r) => (
                                                    <SelectItem key={r} value={r}>
                                                        {r}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label
                                            htmlFor="home-search-modality"
                                            className="text-[11px] font-medium uppercase tracking-wider text-slate-400"
                                        >
                                            Modalidad
                                        </label>
                                        <Select value={searchModality} onValueChange={setSearchModality}>
                                            <SelectTrigger
                                                id="home-search-modality"
                                                data-testid="home-search-modality"
                                                className="h-11 border-slate-800 bg-slate-900 text-slate-100 transition-colors focus:ring-cyan-500/50 data-[state=open]:border-cyan-500/40"
                                            >
                                                <SelectValue placeholder="Todas las modalidades" />
                                            </SelectTrigger>
                                            <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                                                <SelectItem value="all">Todas las modalidades</SelectItem>
                                                {MODALITIES.map((m) => (
                                                    <SelectItem key={m} value={m}>
                                                        {m}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-col justify-end">
                                        <Button
                                            type="submit"
                                            data-testid="home-search-submit"
                                            className="h-11 gap-2 bg-cyan-500 px-6 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition-all hover:bg-cyan-400 hover:shadow-cyan-400/30 md:px-8"
                                        >
                                            Buscar centros
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                <p className="relative mt-4 text-[11px] text-slate-500">
                                    Resultados actualizados diariamente · +88 centros verificados · 16 regiones
                                </p>
                            </div>
                        </form>

                        <div className="mt-8 grid max-w-xl grid-cols-3 gap-6">
                            <div data-testid="stat-centros">
                                <div className="text-3xl font-bold text-cyan-400">88+</div>
                                <div className="text-xs text-slate-400">Centros</div>
                            </div>
                            <div data-testid="stat-profesionales">
                                <div className="text-3xl font-bold text-cyan-400">55+</div>
                                <div className="text-xs text-slate-400">Profesionales</div>
                            </div>
                            <div data-testid="stat-regiones">
                                <div className="text-3xl font-bold text-cyan-400">16</div>
                                <div className="text-xs text-slate-400">Regiones</div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="fade-up-delay-2 relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
                            <img
                                src="https://images.unsplash.com/photo-1584515933487-779824d29309?w=900&q=80"
                                alt="Profesional clínico acompañando a paciente en rehabilitación"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <a
                                    href="https://sinadicciones.org/asesoria"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-asesoria-link"
                                    className="group inline-flex items-center gap-2 text-xs font-medium text-cyan-300 hover:text-cyan-200"
                                >
                                    <Star className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                                    Asesoría experta
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                </a>
                                <h3 className="mt-2 text-xl font-bold text-slate-50">
                                    Tu recuperación empieza con una decisión informada
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="relative border-b border-slate-800 py-20">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,rgba(6,182,212,0.08),transparent_50%)]"
                />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Directorio confiable
                        </div>
                        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                            ¿Por qué elegir un centro verificado?
                        </h2>
                        <p className="mt-3 text-base text-slate-400">
                            La recuperación requiere tratamiento clínico serio.
                            Elegir un centro acreditado reduce recaídas y protege a
                            la persona y su familia.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {benefits.map((b) => (
                            <div
                                key={b.title}
                                data-testid={`benefit-${b.title}`}
                                className="group card-hover relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-6"
                            >
                                {/* Top accent line on hover */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
                                />
                                {/* Subtle glow on hover */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -right-6 -top-8 h-20 w-20 rounded-full bg-cyan-500/0 blur-2xl transition-colors group-hover:bg-cyan-500/20"
                                />
                                <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 transition-colors group-hover:border-cyan-500/40 group-hover:bg-cyan-500/15">
                                    <b.icon className="h-5 w-5" />
                                </div>
                                <h3 className="relative mt-4 text-base font-semibold text-slate-50">
                                    {b.title}
                                </h3>
                                <p className="relative mt-2 text-sm text-slate-400">{b.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured centers */}
            <section className="relative border-b border-slate-800 py-20">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_bottom_left,rgba(6,182,212,0.08),transparent_50%)]"
                />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                <Star className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                                Selección editorial
                            </div>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                                Centros destacados
                            </h2>
                            <p className="mt-2 text-slate-400">
                                Centros verificados con mejor reputación clínica y
                                mayor presencia en nuestras búsquedas.
                            </p>
                        </div>
                        <Link
                            to="/centros"
                            data-testid="home-see-all-centers"
                            className="group inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-cyan-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            Ver todos{" "}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>

                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                  <div
                                      key={i}
                                      className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                  />
                              ))
                            : featured.map((c) => (
                                  <CenterCard key={c.center_id} center={c} />
                              ))}
                    </div>
                </div>
            </section>

            {/* Explora por modalidad */}
            <section className="relative border-b border-slate-800 py-20">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,rgba(6,182,212,0.06),transparent_55%)]"
                />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                            <Activity className="h-3.5 w-3.5" />
                            Tipos de tratamiento
                        </div>
                        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                            Explora por modalidad
                        </h2>
                        <p className="mt-3 text-slate-400">
                            Cada caso clínico requiere la modalidad adecuada. Compara
                            opciones y conoce qué se ofrece en Chile.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        {[
                            { slug: "residencial", name: "Residencial", desc: "Internado 24/7", icon: HomeIcon },
                            { slug: "ambulatoria", name: "Ambulatoria", desc: "Sesiones programadas", icon: Activity },
                            { slug: "online", name: "Online", desc: "Por videollamada", icon: Globe2 },
                            { slug: "mujeres", name: "Para mujeres", desc: "Enfoque de género", icon: HeartPulse },
                            { slug: "adolescentes", name: "Adolescentes", desc: "12-18 años", icon: Sparkles },
                        ].map((m) => (
                            <Link
                                key={m.slug}
                                to={`/modalidades/${m.slug}`}
                                data-testid={`home-modality-${m.slug}`}
                                className="card-hover group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-5"
                            >
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -right-4 -top-6 h-16 w-16 rounded-full bg-cyan-500/0 blur-2xl transition-colors group-hover:bg-cyan-500/20"
                                />
                                <div className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 transition-colors group-hover:border-cyan-500/40">
                                    <m.icon className="h-5 w-5" />
                                </div>
                                <h3 className="relative mt-4 text-base font-semibold text-slate-50">
                                    {m.name}
                                </h3>
                                <p className="relative mt-1 text-xs text-slate-400">{m.desc}</p>
                                <div className="relative mt-4 inline-flex items-center gap-1 text-xs font-medium text-cyan-400">
                                    Explorar
                                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Top ciudades */}
            <section className="relative border-b border-slate-800 py-20">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(6,182,212,0.06),transparent_55%)]"
                />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                <MapPin className="h-3.5 w-3.5" />
                                Cobertura nacional
                            </div>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                                Centros en las principales ciudades
                            </h2>
                            <p className="mt-2 text-slate-400">
                                Información local para encontrar el centro ideal cerca
                                de ti, con contenido editorial específico por ciudad.
                            </p>
                        </div>
                        <Link
                            to="/ciudades"
                            data-testid="home-see-all-cities"
                            className="group inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-cyan-400 hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            Ver 17 ciudades
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                    <div className="mt-10 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {[
                            { slug: "santiago", name: "Santiago", region: "Metropolitana" },
                            { slug: "vina-del-mar", name: "Viña del Mar", region: "Valparaíso" },
                            { slug: "valparaiso", name: "Valparaíso", region: "Valparaíso" },
                            { slug: "concepcion", name: "Concepción", region: "Biobío" },
                            { slug: "antofagasta", name: "Antofagasta", region: "Antofagasta" },
                            { slug: "temuco", name: "Temuco", region: "Araucanía" },
                            { slug: "la-serena", name: "La Serena", region: "Coquimbo" },
                            { slug: "puerto-montt", name: "Puerto Montt", region: "Los Lagos" },
                        ].map((c) => (
                            <Link
                                key={c.slug}
                                to={`/ciudades/${c.slug}`}
                                data-testid={`home-city-${c.slug}`}
                                className="card-hover group flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900 px-5 py-4"
                            >
                                <div>
                                    <div className="text-base font-semibold text-slate-50 group-hover:text-cyan-400">
                                        {c.name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Región {c.region}
                                    </div>
                                </div>
                                <ArrowRight className="h-4 w-4 text-slate-500 transition-all group-hover:translate-x-0.5 group-hover:text-cyan-400" />
                            </Link>
                        ))}
                    </div>

                    {/* Quick comunas Santiago strip */}
                    <div className="mt-10 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                ¿Vives en Santiago?
                            </span>
                            <span className="text-xs text-slate-500">
                                Ve a tu comuna:
                            </span>
                            {[
                                "providencia",
                                "las-condes",
                                "nunoa",
                                "maipu",
                                "puente-alto",
                                "la-florida",
                            ].map((slug) => (
                                <Link
                                    key={slug}
                                    to={`/santiago/${slug}`}
                                    className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-cyan-500/40 hover:text-cyan-400"
                                >
                                    {slug.replace(/-/g, " ").replace(/^./, (m) => m.toUpperCase())}
                                </Link>
                            ))}
                            <Link
                                to="/santiago"
                                className="text-xs font-semibold text-cyan-400 hover:text-cyan-300"
                            >
                                Ver 10 comunas →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="relative border-b border-slate-800 py-20">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.06),transparent_55%)]"
                />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                            <HeartPulse className="h-3.5 w-3.5" />
                            Historias reales
                        </div>
                        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                            Recuperaciones que inspiran
                        </h2>
                        <p className="mt-3 text-slate-400">
                            Pacientes y familias comparten su experiencia con centros
                            verificados.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-5 md:grid-cols-3">
                        {[
                            {
                                name: "María S.",
                                city: "Santiago",
                                text: "Llevo 14 meses sin consumir. El acompañamiento profesional y el plan de seguimiento me cambiaron la vida.",
                            },
                            {
                                name: "Jorge R.",
                                city: "Concepción",
                                text: "Mi familia pudo respirar de nuevo. El centro nos incluyó en todo el proceso con terapia familiar semanal.",
                            },
                            {
                                name: "Camila A.",
                                city: "Valparaíso",
                                text: "Un equipo que te escucha sin juzgar. Recomiendo verificar acreditaciones antes de decidir.",
                            },
                        ].map((t) => (
                            <figure
                                key={t.name}
                                className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-6 transition-all hover:border-cyan-500/40 hover:-translate-y-0.5"
                            >
                                {/* Decorative quotation mark */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute right-4 top-2 select-none font-serif text-7xl leading-none text-cyan-500/10 transition-colors group-hover:text-cyan-500/20"
                                >
                                    &ldquo;
                                </div>
                                <div className="relative flex gap-1 text-amber-300">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star key={i} className="h-4 w-4 fill-amber-300" />
                                    ))}
                                </div>
                                <blockquote className="relative mt-4 text-sm leading-relaxed text-slate-300">
                                    "{t.text}"
                                </blockquote>
                                <figcaption className="relative mt-5 flex items-center gap-2 border-t border-slate-800 pt-4 text-xs text-slate-400">
                                    <MapPin className="h-3.5 w-3.5 text-cyan-400" />
                                    <span className="font-semibold text-slate-200">
                                        {t.name}
                                    </span>{" "}
                                    · {t.city}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-b border-slate-800 py-20" id="faq">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        Preguntas frecuentes
                    </div>
                    <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                        Todo lo que necesitas saber
                    </h2>
                    <p className="mt-3 text-slate-400">
                        Información clave antes de elegir un centro de rehabilitación
                        de drogas en Chile.
                    </p>
                    <div className="relative mt-8 overflow-hidden rounded-2xl p-[1.5px]">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(135deg,rgba(6,182,212,0.35),rgba(15,23,42,0)_60%)]"
                        />
                        <Accordion
                            type="single"
                            collapsible
                            className="rounded-2xl border border-slate-800 bg-slate-900/80 backdrop-blur"
                            data-testid="home-faq"
                        >
                            {faqs.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`faq-${i}`}
                                    className="border-slate-800 px-5 last:border-b-0"
                                >
                                    <AccordionTrigger
                                        data-testid={`home-faq-trigger-${i}`}
                                        className="text-left text-slate-100 hover:text-cyan-400"
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-slate-400">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-2xl p-[1.5px]">
                        {/* Conic gradient border */}
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 -z-10 bg-[conic-gradient(from_120deg_at_50%_50%,rgba(6,182,212,0.6)_0%,rgba(56,189,248,0.3)_30%,rgba(6,182,212,0.1)_60%,rgba(6,182,212,0.6)_100%)]"
                        />
                        <div className="relative overflow-hidden rounded-2xl bg-slate-950 p-10 text-center sm:p-14">
                            {/* Glows */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -left-10 -top-10 h-40 w-60 rounded-full bg-cyan-500/25 blur-3xl"
                            />
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -bottom-16 -right-10 h-44 w-60 rounded-full bg-sky-500/20 blur-3xl"
                            />
                            {/* Dot grid pattern */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:radial-gradient(circle,white_1px,transparent_1px)] [background-size:24px_24px]"
                            />

                            <div className="relative mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                                <Building2 className="h-7 w-7" />
                            </div>
                            <h2 className="relative mt-5 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
                                ¿Administras un centro de rehabilitación?
                            </h2>
                            <p className="relative mx-auto mt-3 max-w-2xl text-slate-400">
                                Regístralo gratis en la plataforma principal
                                SinAdicciones.org y aparece en nuestro directorio
                                público visto por miles de personas cada mes.
                            </p>
                            <div className="relative mt-7 flex flex-wrap items-center justify-center gap-3">
                                <a
                                    href="https://sinadicciones.org/agregar-centro"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="home-cta-register"
                                >
                                    <Button className="h-11 gap-2 bg-cyan-500 px-6 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-500/25 transition-all hover:bg-cyan-400 hover:shadow-cyan-400/35">
                                        Agregar mi centro
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </a>
                                <a
                                    href="https://sinadicciones.org/asesoria"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="home-cta-asesoria"
                                    className="inline-flex h-11 items-center gap-2 rounded-md border border-slate-700 bg-transparent px-5 text-sm font-semibold text-slate-100 transition-colors hover:border-cyan-500/40 hover:bg-slate-900"
                                >
                                    Asesoría experta
                                </a>
                            </div>
                            <p className="relative mt-5 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                                Sin costo · Publicación en minutos · Red SinAdicciones.org
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
